import { ParticipantStepState } from '@wix/ambassador-challenges-v1-participant/types';
import { format, addDays } from 'date-fns';
import { getDelay } from '../../selectors/step';
import {
  StepWithDelay,
  ResolvedStep,
  Status,
  ParticipantStep,
  ParticipantStepV3,
} from '../../types/v3Types';

interface Params {
  step: StepWithDelay;
  resolvedStep?: ResolvedStep;
  initialDate?: Date;
  dateFrame?: ParticipantStep['dateFrame'];
  locale?: any;
  isSelfPaced?: boolean;
}

const transformStep = ({
  step,
  initialDate = null,
  dateFrame = null,
}: Params): ParticipantStep => {
  const currDate = new Date();

  const delay = getDelay(step);
  if (!dateFrame) {
    const startDate = addDays(initialDate || currDate, delay);
    const finishDate = addDays(initialDate || currDate, delay + 1);

    dateFrame = {
      start: format(startDate, 'yyyy-MM-dd'),
      finish: format(finishDate, 'yyyy-MM-dd'),
    };
  }
  return {
    id: step?.id,
    dateFrame,
    source: { ...step },
    transitions: [{ state: ParticipantStepState.RUNNING }],
  };
};

export const resolutionFromResolvedStep = (
  resolvedStep: ResolvedStep,
): ParticipantStep['transitions'] => {
  switch (resolvedStep.status) {
    case Status.COMPLETED:
      return [{ state: ParticipantStepState.COMPLETED }];
    case Status.FAILED:
      return [{ state: ParticipantStepState.FAILED }];
    default:
      return [{ state: ParticipantStepState.RUNNING }];
  }
};

const transitionFromStep = (step: ParticipantStep, isSelfPaced: boolean) => {
  const todayStr = format(new Date(), 'yyyy-MM-dd');

  // PENDING if the step is not started yet.
  // Self-paced programs have no schedule: their `dateFrame.start` is the
  // participant's start date computed by the server in the site timezone,
  // which can be ahead of the member's local date and must not lock steps.
  if (!isSelfPaced && step.dateFrame.start > todayStr) {
    return [{ state: ParticipantStepState.PENDING }];
  }
  // OVERDUE if the finish date is today or in the past
  if (step.dateFrame.finish <= todayStr) {
    return [{ state: ParticipantStepState.OVERDUE }];
  }
  return step.transitions;
};

export function quizSubmissionFromResolvedStep({
  quizSubmissionId,
  earnedScore,
  passingGrade,
}: ResolvedStep): ParticipantStepV3['quizSubmission'] {
  if (!quizSubmissionId) {
    return undefined;
  }
  return {
    quizSubmissionId,
    score: earnedScore,
    passingGrade,
  };
}

export const toParticipantStepV3 = ({
  step,
  resolvedStep,
  initialDate = null,
  dateFrame = null,
  locale = null,
  isSelfPaced = false,
}: Params): ParticipantStep => {
  const _step = transformStep({
    step,
    initialDate,
    locale,
    dateFrame,
  }) as ParticipantStepV3;
  if (dateFrame) {
    _step.dateFrame = dateFrame;
  }
  if (resolvedStep) {
    _step.transitions = resolutionFromResolvedStep(resolvedStep);
    _step.quizSubmission = quizSubmissionFromResolvedStep(resolvedStep);
  } else {
    _step.transitions = transitionFromStep(_step, isSelfPaced);
  }

  return _step;
};

export const toParticipantSteps = ({
  ownerSteps,
  initialDate = null,
}: {
  ownerSteps: { steps: StepWithDelay[] };
  initialDate?: Date;
}): ParticipantStep[] => {
  return ownerSteps.steps.map((step) => transformStep({ step, initialDate }));
};
